import type { QueryClient } from '@tanstack/react-query';
import type { CorSelo } from '../../componentes/base';

export type EstadoRequisicao = 'rascunho' | 'enviada' | 'aprovada' | 'recusada' | 'convertida';
export type EstadoPedido = 'rascunho' | 'enviado' | 'confirmado' | 'recebido_parcial' | 'recebido' | 'cancelado';

export const ESTADOS_REQUISICAO: EstadoRequisicao[] = ['rascunho', 'enviada', 'aprovada', 'recusada', 'convertida'];
export const ESTADOS_PEDIDO: EstadoPedido[] = ['rascunho', 'enviado', 'confirmado', 'recebido_parcial', 'recebido', 'cancelado'];

export const COR_REQUISICAO: Record<EstadoRequisicao, CorSelo> = { rascunho: 'neutro', enviada: 'azul', aprovada: 'verde', recusada: 'perigo', convertida: 'roxo' };
export const COR_PEDIDO: Record<EstadoPedido, CorSelo> = { rascunho: 'neutro', enviado: 'azul', confirmado: 'ciano', recebido_parcial: 'laranja', recebido: 'verde', cancelado: 'perigo' };

export interface Pagina<T> {
  itens: T[];
  proximo_cursor: string | null;
}

export interface Requisicao {
  id: string;
  numero: string;
  solicitante_id: string | null;
  solicitante_nome: string;
  justificativa: string;
  estado: EstadoRequisicao;
  motivo_recusa: string | null;
  decidido_em: string | null;
  itens: number;
  cotacoes: number;
  pedidos: number;
  extras: Record<string, unknown>;
  created_at: string;
}

export interface ItemRequisicao {
  produto_id: string;
  produto_codigo: string | null;
  produto_nome: string | null;
  unidade: string | null;
  quantidade: number;
  observacao: string | null;
}

export interface RequisicaoDetalhe extends Requisicao {
  itens_detalhe: ItemRequisicao[];
  pedidos_ligados: { id: string; numero: string; estado: EstadoPedido }[];
}

export interface Cotacao {
  id: string;
  requisicao_id: string;
  requisicao_numero: string;
  fornecedor_id: string;
  fornecedor_nome: string;
  validade: string | null;
  vencida: boolean;
  condicao_pagamento: string | null;
  observacao: string | null;
  escolhida: boolean;
  moeda: string;
  total_centavos: number;
  prazo_maximo_dias: number | null;
  itens: number;
  created_at: string;
}

export interface ItemCotacao {
  produto_id: string;
  produto_codigo: string | null;
  produto_nome: string | null;
  unidade: string | null;
  quantidade: number;
  preco_unitario_centavos: number;
  prazo_dias: number | null;
  total_centavos: number;
}

export interface CotacaoDetalhe extends Cotacao {
  itens_detalhe: ItemCotacao[];
}

export interface Comparativo {
  requisicao: { id: string; numero: string; estado: EstadoRequisicao };
  cotacoes: { id: string; fornecedor_id: string; fornecedor_nome: string; total_centavos: number; prazo_maximo_dias: number | null; validade: string | null; vencida: boolean; escolhida: boolean; menor_total: boolean; condicao_pagamento: string | null }[];
  produtos: {
    produto_id: string;
    produto_codigo: string | null;
    produto_nome: string | null;
    unidade: string | null;
    quantidade: number;
    ofertas: { cotacao_id: string; preco_unitario_centavos: number; prazo_dias: number | null; total_centavos: number; menor_preco: boolean }[];
  }[];
}

export interface Pedido {
  id: string;
  numero: string;
  fornecedor_id: string;
  fornecedor_nome: string;
  condicao_pagamento: string;
  prazo_entrega: string | null;
  deposito_id: string | null;
  deposito_nome: string | null;
  estado: EstadoPedido;
  total_centavos: number;
  recebido_centavos: number;
  moeda: string;
  itens: number;
  atrasado: boolean;
  dias_atraso: number;
  observacoes: string | null;
  origem: { modulo: string; entidade: string; id: string } | null;
  requisicao_id: string | null;
  requisicao_numero: string | null;
  cotacao_id: string | null;
  motivo_cancelamento: string | null;
  enviado_em: string | null;
  confirmado_em: string | null;
  recebido_em: string | null;
  cancelado_em: string | null;
  extras: Record<string, unknown>;
  created_at: string;
}

export interface ItemPedido {
  id: string;
  produto_id: string;
  produto_codigo: string | null;
  produto_nome: string | null;
  unidade: string | null;
  quantidade: number;
  quantidade_recebida: number;
  saldo: number;
  preco_unitario_centavos: number;
  total_centavos: number;
}

export interface DocumentoGerado {
  id: string;
  vencimento: string;
  valor_centavos: number;
}

export interface RecebimentoResumo {
  id: string;
  numero: string;
  data: string;
  total_centavos: number;
  itens: number;
  documentos: DocumentoGerado[];
  created_at: string;
}

export interface PedidoDetalhe extends Pedido {
  itens_detalhe: ItemPedido[];
  recebimentos: RecebimentoResumo[];
}

export interface Recebimento {
  id: string;
  numero: string;
  pedido_id: string;
  pedido_numero: string;
  fornecedor_id: string;
  fornecedor_nome: string;
  data: string;
  deposito_id: string | null;
  deposito_nome: string | null;
  observacao: string | null;
  total_centavos: number;
  moeda: string;
  itens: number;
  divergente: boolean;
  documentos: DocumentoGerado[];
  created_at: string;
}

export interface ItemRecebido {
  id: string;
  pedido_item_id: string;
  produto_id: string;
  produto_codigo: string | null;
  produto_nome: string | null;
  unidade: string | null;
  quantidade: number;
  preco_unitario_centavos: number;
  total_centavos: number;
  lote_codigo: string | null;
  lote_validade: string | null;
  conferido: boolean;
  observacao: string | null;
  movimentacao_id: string | null;
}

export interface RecebimentoDetalhe extends Recebimento {
  itens_detalhe: ItemRecebido[];
}

export interface Fornecedor {
  id: string;
  nome: string;
  documento: string | null;
  email: string | null;
  telefone: string | null;
  condicao_pagamento: string | null;
  prazo_entrega_dias: number | null;
  observacao_condicao: string | null;
  /** Fase 2 (W25, K14): forma padrão (catálogo do Cadastros) e pedido mínimo, da ficha do parceiro. */
  forma_pagamento_id: string | null;
  forma_pagamento_nome: string | null;
  pedido_minimo_centavos: number | null;
  pedidos: number;
  a_receber: number;
  comprado_centavos: number;
  ultimo_pedido: string | null;
}

export interface ResumoPreco {
  produto_id: string;
  produto_codigo: string | null;
  produto_nome: string | null;
  unidade: string | null;
  fornecedor_id: string;
  fornecedor_nome: string;
  moeda: string;
  compras: number;
  ultimo_centavos: number;
  media_centavos: number;
  menor_centavos: number;
  maior_centavos: number;
  ultima_data: string;
  menor_ultimo: boolean;
}

export interface FornecedorDetalhe extends Fornecedor {
  ultimos_pedidos: { id: string; numero: string; estado: EstadoPedido; total_centavos: number; moeda: string; created_at: string }[];
  precos: ResumoPreco[];
}

export interface DepositoOpcao {
  id: string;
  nome: string;
  padrao: boolean;
}

/** Depois de qualquer escrita: listas, detalhes, comparativo e atividade mudam juntos. O recebimento mexe também em Estoque e Contas. */
export function invalidarCompras(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/compras') }),
    qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/estoque') || q.queryKey[0] === 'estoque' }),
    qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/contas') }),
    qc.invalidateQueries({ queryKey: ['chatter'] }),
  ]);
}

/** Hoje no relógio do navegador (AAAA-MM-DD), sem o desvio de UTC que a noite brasileira causaria. */
export function hojeLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Quantidade com até 4 casas, sem zeros sobrando. */
export function qtd(v: number | null | undefined, numero: (v: number, casas: number) => string) {
  if (v === null || v === undefined) return '';
  const casas = Number.isInteger(v) ? 0 : Math.min(4, (String(v).split('.')[1] ?? '').length);
  return numero(v, casas);
}

/** Texto digitado ("1,5") em número; vazio ou inválido vira null. */
export function numeroDe(texto: string): number | null {
  const t = texto.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Linha de produto nos editores: o preço fica em centavos e o resto em texto para aceitar "1,5" enquanto se digita. */
export interface LinhaItem {
  chave: string;
  produto_id: string;
  produto_rotulo: string;
  quantidade: string;
  preco: number | null;
  prazo: string;
  /** Última compra deste produto com o fornecedor escolhido, ou o custo do Estoque. */
  dica: string;
}

export const novaLinha = (): LinhaItem => ({ chave: crypto.randomUUID(), produto_id: '', produto_rotulo: '', quantidade: '1', preco: null, prazo: '', dica: '' });
